import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpEventType } from '@angular/common/http';
import { Router, RouterModule } from '@angular/router';
import { Subject, concatMap, from, firstValueFrom, timeout, catchError, of } from 'rxjs';

interface ConversionItem {
  file: File;
  status: 'pending' | 'processing' | 'completed' | 'error';
  progress: number;
  previewUrl: string;
  resultUrl?: string;
  error?: string;
}

@Component({
  selector: 'app-webp-converter',
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    <div class="app-layout">
      <!-- Navbar -->
      <nav class="navbar glass">
        <div class="logo">
          <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="2" fill="none"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path></svg>
          Sorrus
        </div>
        <div class="nav-links">
          <a routerLink="/launcher" class="back-link">
            <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" stroke-width="2" fill="none"><polyline points="15 18 9 12 15 6"></polyline></svg>
            Hub
          </a>
          <span class="app-title">WebP Konverter</span>
        </div>
        <div class="avatar">YS</div>
      </nav>

      <main class="main-content">
        <div class="hero glass-panel">
          <div class="hero-content">
            <h1>Bilder in <span class="text-gradient">WebP umwandeln.</span></h1>
            <p>Optimieren Sie Ihre Bilder für das Web. Schneller, kleiner, besser.</p>
            
            <div class="upload-zone" 
                 [class.dragover]="isDragOver"
                 (dragover)="onDragOver($event)"
                 (dragleave)="onDragLeave($event)"
                 (drop)="onDrop($event)"
                 (click)="fileInput.click()">
              <input type="file" #fileInput (change)="onFilesSelected($event)" multiple accept="image/*" style="display: none">
              
              <div class="upload-content">
                <div class="icon-circle">
                  <svg viewBox="0 0 24 24" width="32" height="32" stroke="currentColor" stroke-width="2" fill="none"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>
                </div>
                <h3>Bilder auswählen</h3>
                <p>Klicken oder Bilder hierher ziehen (Bulk möglich)</p>
              </div>
            </div>
          </div>
          <div class="hero-decoration">
            <div class="glow-orb orb-1"></div>
            <div class="glow-orb orb-2"></div>
          </div>
        </div>

        <!-- Conversion List -->
        <div class="conversion-section" *ngIf="items.length > 0">
          <div class="section-header">
            <h2>Dateien ({{ items.length }})</h2>
            <div class="actions" *ngIf="hasCompletedItems()">
              <button class="btn-primary" (click)="downloadAll()">
                <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" stroke-width="2" fill="none"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                Alle als ZIP laden
              </button>
              <button class="btn-secondary" (click)="clearAll()">Liste leeren</button>
            </div>
          </div>

          <div class="items-grid">
            <div class="item-card glass-panel" *ngFor="let item of items; let i = index">
              <div class="item-preview">
                <img [src]="item.previewUrl" alt="Preview">
              </div>
              <div class="item-info">
                <span class="filename">{{ item.file.name }}</span>
                <span class="filesize">{{ formatBytes(item.file.size) }}</span>
                
                <div class="status-container">
                  <div class="progress-bar" *ngIf="item.status === 'processing'">
                    <div class="progress-fill" [style.width]="item.progress + '%'"></div>
                  </div>
                  
                  <span class="status-badge" [ngClass]="item.status">
                    <ng-container [ngSwitch]="item.status">
                      <span *ngSwitchCase="'pending'">Wartend...</span>
                      <span *ngSwitchCase="'processing'">
                        {{ item.progress === 0 ? 'Warte auf Server...' : 'Verarbeite... ' + item.progress + '%' }}
                      </span>
                      <span *ngSwitchCase="'completed'">Abgeschlossen</span>
                      <span *ngSwitchCase="'error'">Fehler: {{ item.error }}</span>
                    </ng-container>
                  </span>
                </div>
              </div>
              <div class="item-actions" *ngIf="item.status === 'completed'">
                <button class="btn-icon" (click)="downloadItem(item)" title="Download">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" stroke-width="2" fill="none"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  `,
  styles: [`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

    :host {
      font-family: 'Inter', sans-serif;
      display: block;
      min-height: 100vh;
      background-color: #0f172a;
      color: #f8fafc;
    }

    .app-layout { display: flex; flex-direction: column; min-height: 100vh; }
    
    .glass {
      background: rgba(15, 23, 42, 0.7);
      backdrop-filter: blur(12px);
      border-bottom: 1px solid rgba(255,255,255,0.05);
    }

    .glass-panel {
      background: rgba(30, 41, 59, 0.5);
      backdrop-filter: blur(16px);
      border: 1px solid rgba(255,255,255,0.05);
      border-radius: 1.5rem;
    }

    .navbar {
      display: flex; justify-content: space-between; align-items: center;
      padding: 1rem 3rem; position: sticky; top: 0; z-index: 100;
    }

    .logo {
      font-size: 1.25rem; font-weight: 700; display: flex; align-items: center; gap: 0.5rem;
      color: #f8fafc;
    }
    .logo svg { color: #38bdf8; }

    .nav-links { display: flex; gap: 2rem; align-items: center; }
    .nav-links a { color: #94a3b8; text-decoration: none; font-weight: 500; transition: color 0.2s; display: flex; align-items: center; gap: 0.25rem; }
    .nav-links a:hover { color: #38bdf8; }
    .back-link { border-right: 1px solid rgba(255,255,255,0.1); padding-right: 1.5rem; margin-right: 0.5rem; }
    .app-title { font-weight: 600; color: #38bdf8; }

    .avatar {
      width: 40px; height: 40px; border-radius: 50%;
      background: linear-gradient(135deg, #38bdf8, #818cf8);
      display: flex; align-items: center; justify-content: center;
      font-weight: 700; color: white;
    }

    .main-content {
      flex: 1; padding: 3rem; max-width: 1200px; margin: 0 auto; width: 100%; box-sizing: border-box;
      display: flex; flex-direction: column; gap: 3rem;
    }

    .hero {
      position: relative; padding: 4rem; overflow: hidden;
      display: flex; align-items: center; justify-content: center;
    }

    .hero-content {
      position: relative; z-index: 10; max-width: 600px; text-align: center;
    }

    .hero h1 { font-size: 3rem; font-weight: 800; line-height: 1.2; margin: 0 0 1rem; }
    .text-gradient {
      background: linear-gradient(to right, #38bdf8, #818cf8);
      -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    }
    .hero p { color: #94a3b8; font-size: 1.1rem; line-height: 1.6; margin-bottom: 2.5rem; }

    .upload-zone {
      border: 2px dashed rgba(56, 189, 248, 0.3); border-radius: 1rem;
      background: rgba(15, 23, 42, 0.4); transition: all 0.3s ease;
      cursor: pointer; position: relative; overflow: hidden;
    }
    .upload-zone:hover, .upload-zone.dragover {
      border-color: #38bdf8; background: rgba(56, 189, 248, 0.05); transform: translateY(-2px);
    }
    .upload-content { padding: 3rem 2rem; display: flex; flex-direction: column; align-items: center; gap: 1rem; }
    .icon-circle {
      width: 64px; height: 64px; border-radius: 50%; background: rgba(56, 189, 248, 0.1);
      color: #38bdf8; display: flex; align-items: center; justify-content: center;
    }
    .upload-content h3 { margin: 0; color: #f8fafc; font-size: 1.2rem; }
    .upload-content p { margin: 0; color: #64748b; font-size: 0.9rem; }

    .hero-decoration { position: absolute; top: 0; left: 0; right: 0; bottom: 0; z-index: 1; pointer-events: none; overflow: hidden; }
    .glow-orb { position: absolute; border-radius: 50%; filter: blur(80px); opacity: 0.5; }
    .orb-1 { top: -100px; left: -100px; width: 300px; height: 300px; background: #0ea5e9; }
    .orb-2 { bottom: -100px; right: -100px; width: 400px; height: 400px; background: #4f46e5; }

    .section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 2rem; }
    .section-header h2 { font-size: 1.5rem; margin: 0; }
    .actions { display: flex; gap: 1rem; }

    .btn-primary {
      background: #38bdf8; color: #0f172a; border: none; padding: 0.75rem 1.5rem; border-radius: 0.75rem;
      font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 0.5rem; transition: all 0.2s;
    }
    .btn-primary:hover { background: #7dd3fc; transform: translateY(-2px); }

    .btn-secondary {
      background: rgba(255,255,255,0.05); color: #f8fafc; border: 1px solid rgba(255,255,255,0.1);
      padding: 0.75rem 1.5rem; border-radius: 0.75rem; font-weight: 600; cursor: pointer; transition: all 0.2s;
    }
    .btn-secondary:hover { background: rgba(255,255,255,0.1); }

    .items-grid { display: flex; flex-direction: column; gap: 1rem; }
    .item-card {
      display: flex; align-items: center; padding: 1.25rem; gap: 1.5rem;
    }

    .item-preview {
      width: 80px; height: 60px; border-radius: 0.75rem; overflow: hidden; background: #1e293b;
      display: flex; align-items: center; justify-content: center;
    }
    .item-preview img { width: 100%; height: 100%; object-fit: cover; }

    .item-info { flex: 1; display: flex; flex-direction: column; gap: 0.25rem; }
    .filename { font-weight: 600; color: #f1f5f9; }
    .filesize { font-size: 0.85rem; color: #64748b; }

    .status-container { display: flex; align-items: center; gap: 1rem; margin-top: 0.5rem; }
    .progress-bar { flex: 1; height: 6px; background: rgba(255,255,255,0.1); border-radius: 3px; overflow: hidden; max-width: 200px; }
    .progress-fill { height: 100%; background: #38bdf8; transition: width 0.3s; }

    .status-badge { font-size: 0.75rem; font-weight: 700; text-transform: uppercase; padding: 0.2rem 0.6rem; border-radius: 0.5rem; }
    .status-badge.pending { color: #94a3b8; background: rgba(148, 163, 184, 0.1); }
    .status-badge.processing { color: #38bdf8; background: rgba(56, 189, 248, 0.1); }
    .status-badge.completed { color: #34d399; background: rgba(52, 211, 153, 0.1); }
    .status-badge.error { color: #f87171; background: rgba(248, 113, 113, 0.1); }

    .btn-icon {
      background: rgba(56, 189, 248, 0.1); color: #38bdf8; border: none; width: 40px; height: 40px;
      border-radius: 0.75rem; cursor: pointer; display: flex; align-items: center; justify-content: center;
      transition: all 0.2s;
    }
    .btn-icon:hover { background: #38bdf8; color: #0f172a; }
  `]
})
export class WebpConverterComponent {
  private http = inject(HttpClient);
  items: ConversionItem[] = [];
  isDragOver = false;
  private queue$ = new Subject<ConversionItem>();

  constructor() {
    this.queue$.pipe(
      concatMap(item => from(this.convertItem(item)).pipe(
        // Ensure at least 200ms between requests for browser connection stability
        concatMap(() => from(new Promise(r => setTimeout(r, 200))))
      ))
    ).subscribe();
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    this.isDragOver = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
    const files = event.dataTransfer?.files;
    if (files) {
      this.addFiles(Array.from(files));
    }
  }

  onFilesSelected(event: any) {
    const files = event.target.files;
    if (files) {
      this.addFiles(Array.from(files));
    }
  }

  addFiles(files: File[]) {
    const newItems = files
      .filter(f => f.type.startsWith('image/'))
      .map(file => ({
        file,
        status: 'pending' as const,
        progress: 0,
        previewUrl: URL.createObjectURL(file)
      }));
    
    this.items = [...this.items, ...newItems];
    newItems.forEach(item => this.queue$.next(item));
  }

  async convertItem(item: ConversionItem): Promise<void> {
    item.status = 'processing';
    item.progress = 0;
    
    const formData = new FormData();
    formData.append('file', item.file);

    try {
      // Use firstValueFrom for more immediate resolution
      const response = await firstValueFrom(
        this.http.post(`/api/media/convert-webp`, formData, {
          responseType: 'blob'
        }).pipe(timeout(30000))
      );
      
      item.status = 'completed';
      item.progress = 100;
      item.resultUrl = URL.createObjectURL(response);
    } catch (err: any) {
      console.error('Conversion error for', item.file.name, err);
      item.status = 'error';
      item.error = err.statusText || err.message || 'Fehler';
    }
  }

  downloadItem(item: ConversionItem) {
    if (item.resultUrl) {
      const a = document.createElement('a');
      a.href = item.resultUrl;
      a.download = item.file.name.split('.').slice(0, -1).join('.') + '.webp';
      a.click();
    }
  }

  async downloadAll() {
    const completed = this.items.filter(i => i.status === 'completed');
    if (completed.length === 0) return;

    const formData = new FormData();
    completed.forEach(item => {
      formData.append('files', item.file);
    });

    this.http.post(`/api/media/convert-bulk-webp`, formData, {
      responseType: 'blob'
    }).subscribe(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sorrus_webp_export.zip';
      a.click();
    });
  }

  clearAll() {
    this.items.forEach(item => {
      if (item.resultUrl) URL.revokeObjectURL(item.resultUrl);
      URL.revokeObjectURL(item.previewUrl);
    });
    this.items = [];
  }

  hasCompletedItems() {
    return this.items.some(i => i.status === 'completed');
  }

  formatBytes(bytes: number, decimals = 2) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }
}
